
import { GET_CURRENT_USER } from '@/graphql';
import type { Student, StudentCourseRegistration } from '@/types/composition-functions';
import { useQuery } from '@vue/apollo-composable';

/**
 * Composable for current user/student data. Each consumer gets its own reactive
 * instance, but Apollo's cache-first policy ensures only one network request.
 * useQuery with cache-first natively deduplicates across all consumers.
 */
export const useUserQuery = () => {
  const includeTertiary = sessionStorage.getItem('school_type')?.toLowerCase() === 'tertiary';

  const {
    result: userResult,
    loading: loadingUser,
    refetch: refetchUser,
    error: errorUser,
    onResult: onResultUser,
  } = useQuery<{ me: Student }>(GET_CURRENT_USER, { include_tertiary: includeTertiary });

  const currentUser = computed(() => userResult.value?.me);
  const isStudent = computed(() => currentUser.value?.__typename === 'Student');
  const studentProfile = computed(() => (isStudent.value ? currentUser.value : null));
  const studentBills = computed(() => studentProfile.value?.studentBills || []);
  const school = computed(() => studentProfile.value?.school || null);
  const currentClassroomSection = computed(() => studentProfile.value?.currentClassroomSection || null);
  const academicCalendars = computed(() => studentProfile.value?.academicCalendars || []);
  const schoolType = computed(() =>
    studentProfile.value?.currentClassroomSection?.school?.school_type?.toLowerCase() ?? null,
  );
  const curriculum = computed(() => studentProfile.value?.studentActiveCurriculum || []);
  const studentregcurrentsemester = computed<StudentCourseRegistration | null>(
    () => studentProfile.value?.studentRegistrationForCurrentSemester ?? null,
  );
  const studentAcademicCalendar = computed(() => studentProfile.value?.studentAcademicCalendar || null);
  const outstandingCourses = computed(() => studentProfile.value?.studentOutstandingCourses ?? null);

  return {
    currentUser,
    loadingUser,
    refetchUser,
    errorUser,
    isStudent,
    studentProfile,
    studentBills,
    currentClassroomSection,
    school,
    academicCalendars,
    curriculum,
    studentregcurrentsemester,
    studentAcademicCalendar,
    outstandingCourses,
    onResultUser,
  };
};
